import type { TFunction } from "i18next";
import { useState, useRef, useEffect } from "react";
import type { AxiosInstance } from "axios";
import type { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { U2000FormStateEnumEditMode } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { useU2000LUStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000LUHandlers";
import { U2000LUStateEnumNextAction } from "../../../../../u2000Core/reducer/formReducer/U2000LUState";
import type { NavigateFunction } from "react-router-dom";
import type { U3148Affiliation, U3148User } from "../../../../U3148Service";

// Gestion des handlers pour les actions d'une unité logique
export interface U3148OrganizationrAffiliationHandler {
  onRowClick: (user: U3148User) => void;
  onImport: () => void;
  onAdd: () => void;
  onModify: () => void;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3148Affiliation>>;
  setDataChangedEvent: (dataChangedEvent: number) => void;
  dataChangedEvent: number;
}

// Paramètres pour les handlers des actions d'une unité logique
export interface U3148OrganizationAffiliationHandlerParam {
  u2000AxiosAuthenticated: AxiosInstance;
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3148Affiliation>>;
  t: TFunction;
  organizationId: string;
  navigate: NavigateFunction;
}

// Gestion des handlers pour les actions d'une unité logique
export interface U3148OrganizationAffiliationHandlerReturn {
  onRowClick: (user: U3148Affiliation) => void;
  onImport: () => void;
  onAdd: () => void;
  onModify: () => void;

  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3148Affiliation>>;
  setDataChangedEvent: (dataChangedEvent: number) => void;
  dataChangedEvent: number;
}

// Implémentation des handlers pour les actions d'une unité logique
export function u3148OrganizationAffiliationHandler(params: U3148OrganizationAffiliationHandlerParam): U3148OrganizationAffiliationHandlerReturn {
  const { luStateHandlers, gridStateHandlers, navigate } = params;
  const [dataChangedEvent, setDataChangedEvent] = useState<number>(0);
  const [localEditMode, _setLocalEditMode] = useState<U2000FormStateEnumEditMode>(U2000FormStateEnumEditMode.NONE);
  const localEditModeRef = useRef(localEditMode);

  useEffect(() => {
    localEditModeRef.current = localEditMode;
  }, [localEditMode]);

  const onRowClick = (user: U3148Affiliation) => {
    luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
    luStateHandlers.setActionOriginFrom(`/U3148/AFFILIATION`);
    const currentIdx = gridStateHandlers ? gridStateHandlers.getData().findIndex((r) => r.id === user.id) : -1;
    if (gridStateHandlers) {
      gridStateHandlers.setCurrentIdx(currentIdx);
    }
    navigate(`affiliation/${user.id}`);
  };

  // Gestion de l'importation d'utilisateurs affiliés
  const onImport = (): void => {
    luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
    luStateHandlers.setActionOriginFrom(`/U3148/ORGANIZATION`);
    navigate("affiliation/import");
  };

  // Gestion de l'ajout d'une action d'unité logique
  const onAdd = (): void => {
    luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
    luStateHandlers.setActionOriginFrom(`/U3148/ORGANIZATION`);
    navigate("affiliation/new");
  };

  // Gestion de la modification d'une action d'unité logique
  const onModify = (): void => {
    luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
    luStateHandlers.setActionOriginFrom(`/U3148/ORGANIZATION`);
    navigate(`affiliation/${gridStateHandlers.getSelectedRows()[0]?.id}`);
  };

  return {
    onImport,
    onRowClick,
    onAdd,
    onModify,

    gridStateHandlers,
    setDataChangedEvent,
    dataChangedEvent,
  };
}
